import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchFeedbackList } from "../../features/feedbackSlice.js";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import Button from "../../components/button/Button.jsx";
import { formatDateTime } from "../../utils/formatDate";

const LIMIT_OPTIONS = [10, 20, 50, 100];

const Feedback = () => {
  const dispatch = useDispatch();
  const { feedbackList, isLoad, pagination } = useSelector(
    (state) => state.feedback
  );

  const [searchText, setSearchText] = useState("");
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const substringValue = (value, maxLength = 100) => {
    let str = "";
    if (value !== null && value !== undefined) {
      str = String(value).substring(0, maxLength);
    }
    return str;
  };

  // Load initial limit from localStorage
  useEffect(() => {
    try {
      const limit = localStorage.getItem("_l");
      if (limit) {
        setLimit(Number(limit));
      }
    } catch (e) {
      setLimit(50);
    }
  }, []);

  // Reset offset when filters/search/limit change
  useEffect(() => {
    setOffset(0);
  }, [searchText, limit]);

  // Fetch list whenever filters, pagination, or search change
  useEffect(() => {
    const handle = setTimeout(() => {
      dispatch(fetchFeedbackList({
        searchText: searchText || undefined,
        limit,
        offset,
      }));
    }, searchText ? 400 : 0);
    return () => clearTimeout(handle);
  }, [dispatch, searchText, limit, offset]);

  // Pagination logic
  const currentPage = Math.floor(offset / limit) + 1;
  const canGoPrev = offset > 0;
  const canGoNext = offset + (feedbackList?.length || 0) < (pagination?.total || 0);

  const nextPage = () => {
    if (canGoNext) setOffset(offset + limit);
  };

  const prevPage = () => {
    if (canGoPrev) setOffset(offset - limit);
  };

  return (
    <div id="main" className="main">
      <Breadcrumb page="Feedback List" />
      <section className="section">
        <div className="row mb-3">
          <div className="col-sm-6 col-md-12 col-lg-12">
            <h6 className="card-title m-0 p-0 mb-3">Feedback List</h6>
            <input
              className="form-control form-control-sm"
              placeholder="Search by email or title..."
              type="text"
              name="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ maxWidth: "300px" }}
            />
          </div>
        </div>

        {isLoad ? (
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th scope="col" className="text-center fs-7">
                    User Email
                  </th>
                  <th scope="col" className="text-center fs-7">
                    Title
                  </th>
                  <th scope="col" className="text-center fs-7">
                    Feedback
                  </th>
                  <th scope="col" className="text-center fs-7">
                    App Version
                  </th>
                  <th scope="col" className="text-center fs-7">
                    Device
                  </th>
                  <th scope="col" className="text-center fs-7">
                    OS Version
                  </th>
                  <th scope="col" className="text-center fs-7">
                    Created At
                  </th>
                </tr>
              </thead>
              <tbody>
                {feedbackList && feedbackList.length ? (
                  feedbackList.map((feedback, i) => (
                    <tr key={`feedback-${i}`}>
                      <td className="text-center fs-7 align-middle">
                        {feedback?.userEmail || "N/A"}
                      </td>
                      <td className="text-center fs-7 align-middle">
                        {substringValue(feedback?.title, 50) || "N/A"}
                      </td>
                      <td className="text-center fs-7 align-middle">
                        <span title={feedback?.feedback}>
                          {substringValue(feedback?.feedback, 100) || "N/A"}
                        </span>
                      </td>
                      <td className="text-center fs-7 align-middle">
                        {feedback?.appVersion || "N/A"}
                      </td>
                      <td className="text-center fs-7 align-middle">
                        {feedback?.device || "N/A"}
                      </td>
                      <td className="text-center fs-7 align-middle">
                        {feedback?.osVersion || "N/A"}
                      </td>
                      <td className="text-center fs-7 align-middle">
                        {formatDateTime(feedback?.createdat)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center fs-7">
                      No Data Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination Bar (Right aligned, one line) */}
            <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
              {/* Limit dropdown */}
              <div className="d-flex align-items-center gap-1">
                <label className="m-0 fs-7">Limit</label>
                <select
                  className="form-select form-select-sm"
                  value={limit}
                  onChange={(e) => {
                    try {
                      localStorage.setItem("_l", e.target.value + "");
                    } catch (e) {}
                    setLimit(Number(e.target.value));
                  }}
                  style={{ width: 80 }}
                >
                  {LIMIT_OPTIONS.map((opt) => (
                    <option key={`limit_${opt}`} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              {/* Prev button */}
              <Button
                className="btn btn-outline-primary btn-sm"
                onClick={prevPage}
                disabled={!canGoPrev}
                title="Previous page"
              >
                Previous
              </Button>

              {/* Current page number */}
              <span className="mx-2 fs-7">Page {currentPage}</span>

              {/* Next button */}
              <Button
                className="btn btn-outline-primary btn-sm"
                onClick={nextPage}
                disabled={!canGoNext}
                title="Next page"
              >
                Next
              </Button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default Feedback;
